import React, { forwardRef, MouseEvent, useMemo } from "react";
import { Page } from "react-pdf";

import { Box } from "@mui/material";
import { FormPolygon, Size, Word, WordPolygon } from "store/types";

import { PDFWordRectangle } from "./PDFWordRectangle";

interface FormRec {
  children: string[];
  id: string;
  css: { left: number; top: number; width: number; height: number };
}

interface WordRec {
  text: string;
  id: string;
  css: { left: number; top: number; width: number; height: number };
}

interface Props {
  pageNumber: number;
  pageSize: Size | undefined;
  originalScale: number;
  userScale: number;
  isVisible: boolean;
  polygons: Array<WordPolygon | FormPolygon> | undefined;
  showPolygons: boolean;
  highlightedPolygonIds: Set<string>;
  polygonTagNumber: Record<string, number>;
  savedPolygonIds: Set<string>;
  selectedWord: Word | null;
  hoveredPolygonId: string | null;
  onPolygonClick: (e: MouseEvent<HTMLDivElement>, rectId: string, text: string) => void;
  onHoverPolygon: (polygonId: string | null) => void;
  onPageSizeResolved: (pageNumber: number, size: Size) => void;
}

const DEFAULT_PAGE_HEIGHT = 800;

const PAGE_SHEET_SX = {
  bgcolor: "white",
  boxShadow: "0px 2px 8px 0px rgba(18, 18, 23, 0.12), 0px 1px 4px 0px rgba(18, 18, 23, 0.08)",
  borderRadius: "2px",
  width: "fit-content",
  overflow: "hidden"
} as const;

export const PDFPageContainer = React.memo(
  forwardRef<HTMLDivElement, Props>(
    (
      {
        pageNumber,
        pageSize,
        originalScale,
        userScale,
        isVisible,
        polygons,
        showPolygons,
        highlightedPolygonIds,
        polygonTagNumber,
        savedPolygonIds,
        selectedWord,
        hoveredPolygonId,
        onPolygonClick,
        onHoverPolygon,
        onPageSizeResolved
      },
      ref
    ) => {
      const scale = originalScale * userScale;
      const computedHeight = pageSize ? pageSize.height * scale : DEFAULT_PAGE_HEIGHT;
      const computedWidth = pageSize ? pageSize.width * scale : "100%";

      const [recs, childRecs, formRecs] = useMemo(() => {
        const recs: Record<string, WordRec> = {};
        const childRecs = new Set<string>();
        const formRecs: FormRec[] = [];

        if (polygons && pageSize) {
          polygons.forEach((poly) => {
            const css = {
              left: poly.boundingBox.Left * pageSize.width * scale,
              top: poly.boundingBox.Top * pageSize.height * scale,
              width: poly.boundingBox.Width * pageSize.width * scale,
              height: poly.boundingBox.Height * pageSize.height * scale
            };

            if ("text" in poly) {
              recs[poly.id] = { text: (poly as WordPolygon).text, id: poly.id, css };
            } else {
              formRecs.push({ children: (poly as FormPolygon).children, id: poly.id, css });
              (poly as FormPolygon).children.forEach((id) => childRecs.add(id));
            }
          });
        }

        return [recs, childRecs, formRecs];
      }, [polygons, pageSize, scale]);

      if (!isVisible) {
        return (
          <Box
            ref={ref}
            data-page={pageNumber}
            sx={{ ...PAGE_SHEET_SX, height: computedHeight, width: computedWidth }}
          />
        );
      }

      return (
        <Box ref={ref} data-page={pageNumber} sx={{ position: "relative", ...PAGE_SHEET_SX }}>
          <Page
            pageNumber={pageNumber}
            scale={scale}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            onLoadSuccess={(page) => {
              const rotate = page.rotate || 0;
              const isRotated = rotate === 90 || rotate === 270;
              onPageSizeResolved(pageNumber, {
                width: isRotated ? page.originalHeight : page.originalWidth,
                height: isRotated ? page.originalWidth : page.originalHeight
              });
            }}
          />
          {showPolygons &&
            Object.keys(recs).map((rectId) => {
              if (!childRecs.has(rectId) && recs[rectId]) {
                return (
                  <PDFWordRectangle
                    key={rectId}
                    rectangle={recs[rectId]}
                    selectedWord={selectedWord}
                    isHighlighted={highlightedPolygonIds.has(rectId)}
                    isHovered={hoveredPolygonId === rectId}
                    tagNumber={polygonTagNumber[rectId]}
                    isSaved={savedPolygonIds.has(rectId)}
                    onClick={onPolygonClick}
                    onMouseEnter={(id) => onHoverPolygon(id)}
                    onMouseLeave={() => onHoverPolygon(null)}
                  />
                );
              }
              return null;
            })}
          {showPolygons &&
            formRecs.map((formRec) => {
              const isWordSelected = selectedWord ? formRec.children.includes(selectedWord.rectId) : false;
              const isFormHighlighted = formRec.children.some((id) => highlightedPolygonIds.has(id));
              return (
                <Box
                  key={formRec.id}
                  sx={{
                    position: "absolute",
                    zIndex: 1,
                    background: isFormHighlighted ? "rgba(209, 230, 204, 0.6)" : "rgba(255, 247, 204, 0.5)",
                    ...(isWordSelected && {
                      background: "rgba(0, 0, 0, 0.10)",
                      opacity: 0.5
                    }),
                    ...formRec.css
                  }}
                >
                  {formRec.children.map((recId: string) =>
                    recs[recId] ? (
                      <PDFWordRectangle
                        key={recId}
                        rectangle={recs[recId]}
                        anchorCSS={formRec.css}
                        selectedWord={selectedWord}
                        isHighlighted={highlightedPolygonIds.has(recId)}
                        isHovered={hoveredPolygonId === recId}
                        tagNumber={polygonTagNumber[recId]}
                        isSaved={savedPolygonIds.has(recId)}
                        onClick={onPolygonClick}
                        onMouseEnter={(id) => onHoverPolygon(id)}
                        onMouseLeave={() => onHoverPolygon(null)}
                      />
                    ) : null
                  )}
                </Box>
              );
            })}
        </Box>
      );
    }
  )
);
